import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { OrganizationApi } from '../../services/ApiService';

function OrganizationList() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await OrganizationApi.getOrganizations();
      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }
      const data = await response.json();
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading organizations:', error);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the organization "${name}"? This action cannot be undone and will affect all associated users.`)) {
      return;
    }

    try {
      const response = await OrganizationApi.deleteOrganization(id);
      if (response.ok) {
        loadOrganizations();
      } else {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Failed to delete (${response.status})`);
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      setError(error.message || 'Failed to delete organization. Please try again.');
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <h2>Organizations</h2>
        <Link to="/settings/organizations/create">
          <button type="button">Create New Organization</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredOrganizations.length === 0 ? (
        <div className="empty-state">
          <h3>No organizations found</h3>
          <p>{searchTerm ? 'No organizations match your search.' : 'No organizations found. Create your first one!'}</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Domain</th>
              <th>Description</th>
              <th>Users</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.map((org, index) => {
              const orgId = org.organizationId || org.organization_id || org.id;
              return (
              <tr key={orgId || index}>
                <td data-label="Organization">
                  <strong>{org.name || org.organization_name || 'Unnamed Organization'}</strong>
                </td>
                <td data-label="Domain">
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'var(--badge-neutral-bg, #e9ecef)',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}>
                    {org.domain || 'No domain'}
                  </span>
                </td>
                <td data-label="Description">{org.description || 'No description'}</td>
                <td data-label="Users">
                  <span style={{
                    color: '#6c757d',
                    fontWeight: '500'
                  }}>
                    {org.userCount || org.user_count || 0} users
                  </span>
                </td>
                <td data-label="Created">
                  {(org.createdAt || org.created_at) ? new Date(org.createdAt || org.created_at).toLocaleDateString() : 'Unknown'}
                </td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link to={`/settings/organizations/${orgId}`}>
                      Edit
                    </Link>
                    <Link to={`/settings/organizations/${orgId}/members`}>
                      Members
                    </Link>
                    <button onClick={() => handleDelete(orgId, org.name || org.organization_name)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="info-panel">
        <h4>About Organizations</h4>
        <p>
          Organizations allow you to group users together for better management and access control. Each organization 
          can have its own domain, settings, and user permissions. Organizations are useful for schools, companies, 
          or any group that needs to share resources and manage learning content collectively.
        </p>
      </div>
    </div>
  );
}

export default OrganizationList;